import CopyToClipboard from 'react-copy-to-clipboard';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePaymentsModalButton } from '@frontend/payments-collection-flow';
import { pendo } from '@frontend/tracking';
import { Button, PopoverMenu, PopoverMenuItem, useAlert, usePopoverMenu } from '@frontend/design-system';
import { styles } from './styles';
import { InvoiceCardActionsProps } from './types';

export function InvoiceCardActions({
  className,
  invoice,
  trackingId,
  onClickRemind,
  onClickCollect,
  onClickView,
  onClickDelete,
}: InvoiceCardActionsProps) {
  const { t } = useTranslation();
  const { disabledButtonProps } = usePaymentsModalButton({ locationIds: [invoice.locationId] });
  const { showLabelWhenDisabled, ...disabledPopoverMenuItemProps } = disabledButtonProps;

  const { getMenuProps, getTriggerProps, getItemProps } = usePopoverMenu();

  const alert = useAlert();

  const handleCollectClick = () => {
    pendo.track('invoice_card_collect_clicked');
    onClickCollect(invoice);
  };

  const handleRemindClick = () => {
    pendo.track('invoice_card_remind_clicked');
    onClickRemind(invoice);
  };

  const handleViewClick = () => {
    pendo.track('invoice_card_view_clicked');
    onClickView(invoice);
  };

  const handleDeleteClick = () => {
    pendo.track('invoice_card_delete_clicked');
    onClickDelete(invoice);
  };

  return (
    <div className={`card__actions ${className || ''}`} css={styles.actions}>
      <div css={styles.actionFullButton}>
        <Button
          variant='secondary'
          trackingId={`${trackingId}--invoice-card--collect`}
          onClick={handleCollectClick}
          iconName='pay'
          {...disabledButtonProps}
        >
          {t('Collect')}
        </Button>
      </div>
      <div css={styles.actionFullButton}>
        <Button
          variant='secondary'
          trackingId={`${trackingId}--invoice-card--remind`}
          onClick={handleRemindClick}
          iconName='notification'
          {...disabledButtonProps}
        >
          {t('Remind')}
        </Button>
      </div>
      <div css={styles.actionMiniButton}>
        <Button variant='secondary' iconName='more' {...getTriggerProps()} />
        <PopoverMenu {...getMenuProps()}>
          <CopyToClipboard
            text={invoice?.links.payment ?? ''}
            onCopy={() => {
              alert.success(t('Payment link copied!'));
            }}
          >
            <PopoverMenuItem
              {...getItemProps({ index: 0, onClick: () => {} })}
              trackingId={`${trackingId}--invoice-card--copy-link`}
              Icon={() => <Icon name='link' size={16} />}
              {...disabledPopoverMenuItemProps}
            >
              {t('Copy Request Link')}
            </PopoverMenuItem>
          </CopyToClipboard>
          <PopoverMenuItem
            {...getItemProps({ index: 1, onClick: handleViewClick })}
            trackingId={`${trackingId}--invoice-card--view-request`}
            Icon={() => <Icon name='arrow-right-small' size={16} />}
          >
            {t('View Request in Payments')}
          </PopoverMenuItem>
          <PopoverMenuItem
            {...getItemProps({ index: 2, onClick: handleDeleteClick })}
            trackingId={`${trackingId}--invoice-card--delete-request`}
            Icon={() => <Icon name='trash' size={16} />}
            destructive
          >
            {t('Delete Request')}
          </PopoverMenuItem>
        </PopoverMenu>
      </div>
    </div>
  );
}
